import React from "react";
import { Dialog } from "@headlessui/react";
import { useTranslation } from "react-i18next";

import Alert from "@components/shared/Alert";

/**
 * A modal form: title, fields, then Cancel and submit. Always open; mount it
 * only while needed, so its form starts from current values each time.
 */
const FormDialog: React.FunctionComponent<{
  title: React.ReactNode;
  submitLabel: React.ReactNode;
  onClose: () => void;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  busy?: boolean;
  /** Shown above the fields, e.g. a failed save. */
  error?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, submitLabel, onClose, onSubmit, busy, error, children }) => {
  const { t } = useTranslation();

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-gray-500/75" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl">
            <form onSubmit={onSubmit} noValidate>
              <Dialog.Title className="border-b border-gray-200 px-5 py-3.5 text-sm font-bold text-primary-3">
                {title}
              </Dialog.Title>
              <div className="space-y-4 p-5">
                {error ? <Alert type="error">{error}</Alert> : null}
                {children}
              </div>
              <footer className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
                <button type="button" className="btn-default" onClick={onClose}>
                  {t("common.cancel")}
                </button>
                <button type="submit" className="btn-primary" disabled={busy}>
                  {submitLabel}
                </button>
              </footer>
            </form>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

export default FormDialog;
